import { FormBlock, FormCard, ValidationList } from '@qbe/ads-blocks-forms';
import {
  Box,
  Button,
  RadioSelectableCard,
  RadioSelectableCardProps,
  Spinner,
  Typography,
} from '@qbe/ads-core';
import startCase from 'lodash/startCase';
import React, { useEffect, useMemo } from 'react';

import { MONEY_SI_OPTIONS } from '~/constants/domain-specific';
import { Option, useAppContent, useAppDispatch, useCoverDetails, useOptionSelect } from '~/hooks';
import { useAppNavigate } from '~/hooks/useAppNavigate';
import { useCoverDetailsEffects } from '~/hooks/useCoverDetailsEffects';
import { useTrackOptimizeActivate } from '~/hooks/useTrackOptimizeActivate';
import { updateMoneySI } from '~/slices/coverDetails';
import { addValidatedCover, removeValidatedCover } from '~/slices/selectCover';
import { qalFormCompleteAction, qalFormStartAction } from '~/thunks/analytics';
import { Cover } from '~/types/cover';
import { isFormStartStarted } from '~/utils/analytics/trackUtils';
import { CurrencyFormat, currencyFormatter } from '~/utils/currencyFormatter';

const options: Option[] = MONEY_SI_OPTIONS.map(({ text, value }) => ({
  id: value,
  value: startCase(text),
}));

const QuestionSI: React.FC = () => {
  const { getTextContent } = useAppContent({ basePath: 'dsme.coverDetailsMoney' });
  const dispatch = useAppDispatch();
  const navigate = useAppNavigate();
  const { moneySI } = useCoverDetails();
  const { selectedIDs, onSelectID, setSelectedIDs } = useOptionSelect({
    options,
  });

  useEffect(() => {
    if (moneySI) setSelectedIDs([moneySI.toString()]);
  }, [moneySI, setSelectedIDs]);

  const handleFormStart = () => {
    if (!isFormStartStarted()) dispatch(qalFormStartAction());
  };

  const onListItemChange = (id: string) => {
    if (!selectedIDs.includes(id)) {
      onSelectID(id);
      dispatch(updateMoneySI(Number(id)));
      navigate('#next');
    }
    handleFormStart();
  };

  const radioList = useMemo(
    () =>
      options.map(({ id, value }) => ({
        id,
        value,
        label: currencyFormatter(value, CurrencyFormat.Comma),
        labelProps: {
          variant: 'label2',
          weight: 'bold',
        },
        'data-track': currencyFormatter(value, CurrencyFormat.Comma),
        'data-track-value': 'capture',
      })) as RadioSelectableCardProps['radioList'],
    []
  );

  return (
    <RadioSelectableCard
      defaultValue={moneySI}
      direction="vertical"
      name="name"
      radioList={radioList}
      title={getTextContent('fields.sumInsured.title')}
      onChange={onListItemChange}
      fullWidth
    />
  );
};

const CoverDetailsMoney: React.FC = () => {
  const { getTextContent: getSharedContent } = useAppContent({
    basePath: 'shared',
  });

  const { getTextContent } = useAppContent({ basePath: 'dsme.coverDetailsMoney' });
  const dispatch = useAppDispatch();
  const { isValidMoney } = useCoverDetails();
  const { quotePremiums, isLoading, buttonText } = useCoverDetailsEffects({
    cover: Cover.Money,
  });

  const handleFormComplete = () => {
    if (isFormStartStarted()) dispatch(qalFormCompleteAction());
  };

  /** @clarify is there a soft decline? */
  const handleNext = (e: React.MouseEvent<HTMLElement>) => {
    e.preventDefault();
    handleFormComplete();
    quotePremiums();
  };

  useEffect(() => {
    !isValidMoney
      ? dispatch(removeValidatedCover(Cover.Money))
      : dispatch(addValidatedCover(Cover.Money));
  }, [dispatch, isValidMoney]);

  // Tracks the "optimize.activate" event when the component mounts or re-renders.
  // This is used to notify Webtrends (or other analytics tools) for visibility tracking
  // and ensures dynamic content is properly handled.
  useTrackOptimizeActivate();

  return (
    <FormCard variant="sectionTitle" title={getTextContent('title')}>
      <Typography variant="body1" component="h1" fontWeight="fontWeightMedium" gutterBottom>
        {getTextContent('fields.inclusions.title')}
      </Typography>

      <ValidationList
        list={[
          {
            id: 'e1',
            item: 'Money on premises during business hours',
          },
          {
            id: 'e2',
            item: 'Money on premises outside business hours',
          },
          {
            id: 'e3',
            item: 'Money on premises, while kept in ATMs or locked safes or strongrooms',
          },
          {
            id: 'e4',
            item: 'Money at your or your employees residences',
          },
          {
            id: 'e6',
            item: 'Money in transit (anywhere in Australia)',
          },
        ]}
      />

      <FormBlock preset="default" maxWidth="100%">
        <QuestionSI />
      </FormBlock>

      <Spinner loading={isLoading} description={getSharedContent('loadingLabel')} />
      <Box id="next">
        <FormBlock preset="button">
          <Button
            data-track={`${buttonText} - money`}
            disabled={!isValidMoney || isLoading}
            onClick={handleNext}
            label={buttonText}
          />
        </FormBlock>
      </Box>
    </FormCard>
  );
};

export default CoverDetailsMoney;
